import { useQuery } from '@tanstack/react-query';
import type { VideoTimeline } from '@chatsdet/types';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type WorkflowRunThoughtDto = {
  id: string;
  runId: string;
  stepId: string | null;
  nodeId: string | null;
  content: string;
  toolName: string | null;
  orderIndex: number;
  /** When content first started arriving from the model (null for older thoughts) */
  startedAt: string | null;
  createdAt: string;
};

type WorkflowRunThoughtsResponse = {
  ok: true;
  data: { thoughts: WorkflowRunThoughtDto[] };
};

export function useWorkflowRunThoughts(workflowId: string | null, runId: string | null) {
  return useQuery({
    queryKey: ['workflow-run-thoughts', workflowId, runId],
    enabled: Boolean(workflowId && runId),
    queryFn: async () => {
      if (!workflowId || !runId) throw new Error('Missing workflowId or runId');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}/thoughts`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch workflow run thoughts');
      const data = (await r.json()) as WorkflowRunThoughtsResponse;
      if (!data.ok) throw new Error('Failed to fetch workflow run thoughts');
      return data.data.thoughts;
    },
  });
}

/**
 * Group thoughts by nodeId for inline display per step.
 * Returns a Map where key is nodeId and value is array of thoughts.
 */
export function groupThoughtsByNode(
  thoughts: WorkflowRunThoughtDto[]
): Map<string, WorkflowRunThoughtDto[]> {
  const grouped = new Map<string, WorkflowRunThoughtDto[]>();
  for (const thought of thoughts) {
    const key = thought.nodeId ?? '__unassigned__';
    const existing = grouped.get(key) ?? [];
    existing.push(thought);
    grouped.set(key, existing);
  }
  return grouped;
}

/**
 * Thought with calculated timestampMs for video synchronization.
 */
export type ThoughtWithTimestamp = WorkflowRunThoughtDto & {
  timestampMs: number;
};

/**
 * Calculate timestampMs for thoughts relative to video start.
 * Used to synchronize thoughts with video timeline.
 *
 * Wait nodes introduce wall-clock gaps that are stitched out of the video.
 * We subtract accumulated wait durations so thoughts after waits align
 * with the correct video position.
 *
 * @param thoughts - Array of thoughts with ISO createdAt
 * @param timeline - Video timeline with entries (including wait annotations)
 * @returns Thoughts with calculated timestampMs
 */
export function calculateThoughtTimestamps(
  thoughts: WorkflowRunThoughtDto[],
  timeline: VideoTimeline | null
): ThoughtWithTimestamp[] {
  if (thoughts.length === 0) return [];

  if (!timeline) {
    // No video reference - use first thought as t=0
    const firstThoughtTime = new Date(thoughts[0].startedAt ?? thoughts[0].createdAt).getTime();
    return thoughts.map((thought) => ({
      ...thought,
      timestampMs: Math.max(0, new Date(thought.startedAt ?? thought.createdAt).getTime() - firstThoughtTime),
    }));
  }

  const startMs = new Date(timeline.startTimestamp).getTime();

  // Collect wait annotations sorted by time
  const waitGaps = timeline.entries
    .filter((e) => e.annotationType === 'wait' && e.waitDurationSeconds)
    .map((e) => ({
      isoMs: new Date(e.isoTimestamp).getTime(),
      durationMs: e.waitDurationSeconds! * 1000,
    }));

  return thoughts.map((thought) => {
    // Use startedAt (when content first arrived) for more accurate video alignment.
    // Falls back to createdAt for older thoughts that don't have startedAt.
    const thoughtMs = new Date(thought.startedAt ?? thought.createdAt).getTime();

    // Sum wait durations for all waits that occurred before this thought
    const totalWaitMs = waitGaps.reduce(
      (sum, gap) => (gap.isoMs <= thoughtMs ? sum + gap.durationMs : sum),
      0
    );

    return {
      ...thought,
      timestampMs: Math.max(0, thoughtMs - startMs - totalWaitMs),
    };
  });
}
